import './Style.scss';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DashboardInterface } from 'Interfaces/DashBoardInterface';
import { StateInterface } from 'Interfaces/StateInterface';
import { ChartModel } from 'Interfaces/ChartInterface';
import { ConfirmationModalProps } from 'views/Modals/ConfirmationModal';
import { ModalComponentNames, ModalInterface } from 'Interfaces/ModalInterface';
import { DataPoint } from 'Interfaces/DataPoint';
import * as React from 'react';
import { Responsive, LayoutItem, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { fetchPOI } from 'actions/pois';
import { getLocalization } from 'global/global';
import { deleteDashboard, removeDataPointFromDashboard, setShowOnLogin, updateLayout } from 'actions/dashboardActions';
import { useAppDispatch, useAppSelector } from 'index';
import { navigateAddModal, navigateRemoveModal } from 'actions/navigationAction';
import DashboardChart from './DashboardChart';
import { AddChartToDashboardModalProps } from './AddChartToDashboardModal';
import { AddDataPointToDashboardModalProps } from './AddDataPointToDashboardModal';
import DashboardDataPoint from './DashboardDataPoint';
import { getLayout } from './util';

export default function Dashboard() {
  const dispatch = useAppDispatch();
  const abortController = React.useRef(new AbortController());
  const dashboard = useSelector((state: StateInterface) => state.dashboard);
  const charts = useSelector((state: StateInterface) => state.chartsMenu.charts);
  const dashboardMenu = useSelector((state: StateInterface) => state.dashboardMenu);
  const clientPersist = useAppSelector(state => state.clientPersist);
  const forms = useAppSelector((state: StateInterface) => state.forms.collection);

  const [selectedDashboard, setSelectedDashboard] = React.useState<DashboardInterface | undefined>(undefined);
  const [dashboardCharts, setDashboardCharts] = React.useState<ChartModel[]>([]);
  const [layout, setLayout] = React.useState<{lg: LayoutItem[]}>({ lg: []});
  const [applyFilters, setApplyFilters] = React.useState(false);

  const [dataPoints, setDataPoints] = React.useState<DataPoint[]>([]);

  React.useEffect(() => {
    if (dashboardMenu.active) {
      const selected = dashboard.collection.find(d => `${d.id}` === dashboardMenu.active);
      setSelectedDashboard(selected);
    } else {
      setSelectedDashboard(undefined);
    }
  }, [dashboardMenu.active, dashboard]);

  React.useEffect(() => {
    const chart = getCharts().filter(c => selectedDashboard?.chartIds?.includes(Number(c.id)));
    if (selectedDashboard) {
      if (selectedDashboard.layout) {
        if (JSON.stringify(selectedDashboard.layout) !== JSON.stringify(layout)) {
          setLayout({lg: selectedDashboard.layout});
        }
        // resetLayout();
      } else {
        const column = selectedDashboard.columns || 2;
        const lo = chart.map(c => `${c.id}`).concat(selectedDashboard.dataPoints.map(d => d.id)).map((c, i) => {
          return getLayout(`${c}`, i, column);
        });
        // setLayout(lo);
        void dispatch(updateLayout({ dashboardId: selectedDashboard.id, layout: lo, columns: column }));
      }
    }
    setDashboardCharts(chart);
  }, [selectedDashboard?.layout, selectedDashboard?.columns, charts]);

  React.useEffect(() => {
    setDataPoints([]);
    abortController.current.abort();
    abortController.current = new AbortController();
  }, [selectedDashboard?.id]);

  React.useEffect(() => {
    if (selectedDashboard) {
      const formsData = {};
      selectedDashboard.dataPoints.forEach(dp => {
        formsData[dp.questionnaireId] = formsData[dp.questionnaireId] || [];
        formsData[dp.questionnaireId].push(dp.id);
      });
      const formIds = Object.keys(formsData);
      let dps: DataPoint[] = [];

      const fetchData = async () => {
        for (const formId of formIds) {
          const form = forms.find(f => f.ref === formId);
          if (form) {
            const query = { id: formsData[formId].join(',') };
            const request = dispatch(fetchPOI(
              formId,
              abortController.current.signal,
              undefined,
              [],
              query,
            ));
            const json = await (await request).json();
            if (Array.isArray(json)) {
              dps = dps.concat(json);
            }
          }
        }
        setDataPoints(dps);
      };
      void fetchData();
    } else {
      setDataPoints([]);
    }
  }, [selectedDashboard?.dataPoints, forms]);

  const getCharts = () => {
    const formIds = forms.map(f => f.ref);
    return charts.filter(c => {
      const chartForms = Object.keys(c.forms);
      const missing = chartForms.some(e => formIds.indexOf(e) === -1);
      if (missing) {
        return false;
      }
      return selectedDashboard?.chartIds?.includes(Number(c.id));
    });
  };

  const resetLayout = (columns?: number) => {
    if (selectedDashboard) {
      const chart = getCharts().concat(selectedDashboard.dataPoints);
      const cols = columns ? columns : selectedDashboard.columns || 2;
      const lo = chart.map((c, i) => {
        return getLayout( `${c.id}`, i, cols);
      });
      void dispatch(updateLayout({ dashboardId: selectedDashboard.id, layout: lo, columns: cols }));
    }
  };

  const addChart = () => {
    if (selectedDashboard) {
      const modalProps: ModalInterface<AddChartToDashboardModalProps> = {
        component: ModalComponentNames.AddChartToDashboardModal,
        props: {
          onClose: () => {
            dispatch(navigateRemoveModal(ModalComponentNames.AddChartToDashboardModal));
          },
          dashboardId: selectedDashboard.id,
          currentCharts: selectedDashboard.chartIds || []
        }
      };
      dispatch(navigateAddModal(modalProps));
    }
  };

  const addDataPoint = () => {
    if (selectedDashboard) {
      const modalProps: ModalInterface<AddDataPointToDashboardModalProps> = {
        component: ModalComponentNames.AddDataPointToDashboardModal,
        props: {
          onClose: () => {
            dispatch(navigateRemoveModal(ModalComponentNames.AddDataPointToDashboardModal));
          },
          dashboardId: selectedDashboard.id,
          dataPoints: selectedDashboard.dataPoints
        }
      };
      dispatch(navigateAddModal(modalProps));
    }
  };

  /* const onResizeStop = (layout: ReactGridLayout.Layout[], oldItem, newItem,
    placeholder, e: MouseEvent, element: HTMLElement) => {
    console.log(element);
  };*/

  const onSetShowOnLogin = (checked: boolean) => {
    if (selectedDashboard) {
      void dispatch(setShowOnLogin({dashboardId: selectedDashboard?.id, showOnLogin: checked}));
    }
  };

  const onRemoveDashboard = () => {
    const modalProps: ModalInterface<ConfirmationModalProps> = {
      component: ModalComponentNames.ConfirmationModal,
      props: {
        onClose: () => {
          dispatch(navigateRemoveModal(ModalComponentNames.ConfirmationModal));
        },
        onConfirm: () => {
          if (selectedDashboard) {
            void dispatch(deleteDashboard({
              dashboardId: selectedDashboard?.id,
            }));
          }
          dispatch(navigateRemoveModal(ModalComponentNames.ConfirmationModal));
        },
        localizations: {
          cancel: getLocalization('cancel'),
          confirm: getLocalization('remove'),
          confirmStyle: 'danger',
          header: (<label>{getLocalization('confirm')}</label>),
          body: (<p>{getLocalization('deleteDashboard')}</p>)
        }
      }
    };
    dispatch(navigateAddModal(modalProps));
  };

  const onRemoveDataPoint = (dashboardId: number, dataPointId: string) => {
    const modalProps: ModalInterface<ConfirmationModalProps> = {
      component: ModalComponentNames.ConfirmationModal,
      props: {
        onClose: () => {
          dispatch(navigateRemoveModal(ModalComponentNames.ConfirmationModal));
        },
        onConfirm: () => {
          void dispatch(removeDataPointFromDashboard({
            dashboardId, dataPointId
          }));
          dispatch(navigateRemoveModal(ModalComponentNames.ConfirmationModal));
        },
        localizations: {
          cancel: getLocalization('cancel'),
          confirm: getLocalization('remove'),
          confirmStyle: 'danger',
          header: (<label>{getLocalization('confirm')}</label>),
          body: (<p>{getLocalization('removeDataPoint')}</p>)
        }
      }
    };
    dispatch(navigateAddModal(modalProps));
  };

  const onDataPointUpdated = (dataPoint: DataPoint) => {
    setDataPoints(dataPoints.map((dp) => {
      if (dp.id && dp.id === dataPoint.id) {
        return {...dataPoint};
      }
      return dp;
    }));
  };

  const setColumns = (columns: number) => {
    if (selectedDashboard && selectedDashboard.columns !== columns && selectedDashboard.layout) {
      resetLayout(columns);
      /* void dispatch(updateLayout({
        dashboardId: selectedDashboard.id, layout: selectedDashboard.layout, columns: columns
      }));*/
    }
  };

  const handleLayoutChange = React.useCallback((layout, allLayouts) => {
    console.log(allLayouts);
    if (selectedDashboard) {
      setLayout(allLayouts);
      void dispatch(updateLayout({
        dashboardId: selectedDashboard.id, layout: allLayouts.lg, columns: selectedDashboard.columns || 2
      }));
    }
  }, []);

  const children = React.useMemo(() => {
    return dashboardCharts && selectedDashboard ? (
      dashboardCharts?.map(chart => {
        return (
          <div key={`${chart.id}`} className="grid-item">
            <DashboardChart
              chart={chart}
              dashboardId={selectedDashboard.id}
              layout={selectedDashboard.layout || []}
              applyFilters={applyFilters}
              canEdit={selectedDashboard.userId === Number(clientPersist.user_id)}
              onChartLoaded={() => setLayout([...layout])}
            />
          </div>
        );
      }).concat(forms.length ? dataPoints.map(dp => (
        <div key={`${dp.id}`} className="grid-item">
          <div className="col dashboard-chart-container">
            <DashboardDataPoint
              key={dp._id}
              dataPoint={dp}
              dashboard={selectedDashboard}
              onRemoveDataPoint={onRemoveDataPoint}
              onDataPointUpdated={onDataPointUpdated}
            />
          </div>
        </div>
      )) : [])
    ) : [];
  }, [dashboardCharts, dataPoints, forms, layout, selectedDashboard?.columns]);

  // const cols = selectedDashboard ? selectedDashboard.columns || 2 : 2;
  // const lo = selectedDashboard && selectedDashboard?.layout ? selectedDashboard.layout : [];

  const { width, containerRef, mounted } = useContainerWidth();

  return (
    <div className="main-dashboard">
      {dashboardMenu.active ? (
        <>
          <div className="dashboard-container">
            <h5 className="me-3 dashboard-name">{selectedDashboard?.name}</h5>
            {selectedDashboard?.userId === Number(clientPersist.user_id) && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={addChart}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  <span className="add-chart-span">{getLocalization('addChart')}</span>
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={addDataPoint}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  <span className="add-chart-span">{getLocalization('addDataPoint')}</span>
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant={applyFilters ? 'success' : 'secondary'}
              onClick={() => setApplyFilters(!applyFilters)}
            >
              <i className="fa fa-refresh"/>
              <span className="add-chart-span">{getLocalization('apply_filters')}</span>
            </Button>
            <Button
              size="sm"
              variant={'success'}
              onClick={() => resetLayout()}
            >
              <i className="fa fa-refresh"/>
              <span className="add-chart-span">{getLocalization('resetLayout')}</span>
            </Button>
            <Dropdown className="btn-group">
              <Dropdown.Toggle
                id="dashboard-columns-btn-toggle"
                variant="primary"
                size={'sm'}
              >
                {getLocalization('columns')}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  as="div"
                  eventKey={`2`}
                  active={selectedDashboard?.columns === 2}
                  onClick={() => setColumns(2)}
                >2</Dropdown.Item>
                <Dropdown.Item
                  as="div"
                  eventKey={`3`}
                  active={selectedDashboard?.columns === 3}
                  onClick={() => setColumns(3)}
                >3</Dropdown.Item>
                <Dropdown.Item
                  as="div"
                  eventKey={`4`}
                  active={selectedDashboard?.columns === 4}
                  onClick={() => setColumns(4)}
                >4</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            {selectedDashboard?.userId === Number(clientPersist.user_id) && (
              <>
                <Button
                  size="sm"
                  onClick={onRemoveDashboard}
                  variant={'danger'}
                >
                  <i className="fa fa-trash"/>
                </Button>
                <Form.Check
                  id="show-after-login-checkbox"
                  name={'show-after-login'}
                  className="ms-2"
                  checked={selectedDashboard?.showOnLogin}
                  onChange={(e) => onSetShowOnLogin(e.target.checked)}
                  label={getLocalization('showOnLogin')}
                />
              </>
            )}
          </div>
          <div
            className="dashboard-container"
            ref={containerRef}
          >
            {mounted && (
              <Responsive
                className="layout"
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                layouts={layout}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                width={width}
                rowHeight={150}
                compactor={verticalCompactor}
                useCSSTransforms={true}
                onLayoutChange={handleLayoutChange}
                isResizable={false}
              >
                {children}
              </Responsive>
            )}
            {/* <ResponsiveGridLayout
              cols={{ lg: cols, md: cols, sm: 1, xs: 1, xxs: 1 }}
              layouts={{lg: [...lo], md: [...lo], sm: [...lo]}}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              rowHeight={130}
              isResizable={false}
              onLayoutChange={(layout: ReactGridLayout.Layout[]) => {
                onUpdateLayout(layout);
              }}
              onResizeStop={onResizeStop}
              compactType={'horizontal'}
              measureBeforeMount={false}
              useCSSTransforms={false}
            >
            </ResponsiveGridLayout>*/}
          </div>
        </>
      ) : (
        <p>{getLocalization('selectDashboard')}</p>
      )}
    </div>
  );
}
